import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  IconButton,
  Collapse,
  Tabs,
  Tab
} from '@mui/material';
import {
  History as HistoryIcon,
  ExpandLess,
  ExpandMore
} from '@mui/icons-material';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Description } from '@mui/icons-material';

interface ConsultationHistoryItem {
  id: string;
  category: string;
  original_question: string;
  status: string;
  confidence_level?: string;
  created_at: string;
  updated_at: string;
  has_final_response: boolean;
  metadata?: {
    total_results?: number;
    search_results?: any[];
    search_time_ms?: number;
  };
}

interface ConsultationHistoryProps {
  onSelectConversation: (conversationId: string) => void;
  selectedConversationId?: string;
}

const ConsultationHistory: React.FC<ConsultationHistoryProps> = ({
  onSelectConversation,
  selectedConversationId
}) => {
  const [history, setHistory] = useState<ConsultationHistoryItem[]>([]);
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [expanded, setExpanded] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('consultation');
  // Documents history state
  interface DocumentHistoryItem {
    filename: string;
    file_size: number;
    generated_at: string;
    download_url: string;
    contract_type: string;
  }
  const [docHistory, setDocHistory] = useState<DocumentHistoryItem[]>([]);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      loadHistory();
    }
  }, [isAuthenticated, user?.user_id]);

  // Ensure documents history loads on initial render when default tab is 'documents'
  useEffect(() => {
    if (activeTab === 'documents') {
      if (docHistory.length === 0 && !docLoading) {
        loadDocHistory();
      }
    } else {
      // For all non-document tabs, refresh consultation history when user switches tabs
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Auto-refresh when window regains focus or tab becomes visible
  useEffect(() => {
    const onFocus = () => {
      if (activeTab === 'documents') {
        loadDocHistory();
      } else {
        loadHistory();
      }
    };
    const onVisibility = () => {
      if (!document.hidden) onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const loadHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await api.get('/consultation/history', {
        params: { limit: 20, offset: 0, user_id: user?.user_id || undefined }
      });

      if (response.data.success) {
        setHistory(response.data.conversations || []);
      }
    } catch (err: any) {
      if (err.response?.status === 401) {
        setError('Pro zobrazení historie se prosím přihlaste');
      } else {
        setError('Chyba při načítání historie konzultací');
      }
      console.error('Failed to load consultation history:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadDocHistory = async () => {
    setDocLoading(true);
    setDocError('');
    try {
      try {
        const resp = await api.get('/documents/history?limit=50');
        const list = resp.data || [];
        if (Array.isArray(list) && list.length > 0) {
          setDocHistory(list);
        } else {
          const resp2 = await fetch('/api/v1/documents/test/history?limit=50');
          if (resp2.ok) {
            const data = await resp2.json();
            setDocHistory(data || []);
          } else {
            setDocHistory([]);
          }
        }
      } catch (e) {
        const resp = await fetch('/api/v1/documents/test/history?limit=50');
        if (resp.ok) {
          const data = await resp.json();
          setDocHistory(data || []);
        } else {
          throw new Error('Failed to load');
        }
      }
    } catch (err) {
      setDocError('Chyba při načítání historie dokumentů');
    } finally {
      setDocLoading(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      completed: 'Dokončeno',
      awaiting_clarification: 'Čeká na upřesnění',
      clarification_received: 'Zpracování',
      pending: 'Čeká',
      processing: 'Zpracování',
      failed: 'Chyba'
    };
    return labels[status] || status;
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      housing: 'Bydlení',
      work: 'Práce',
      family: 'Rodina',
      accidents: 'Nehody',
      documents: 'Dokumenty',
      general: 'Obecné'
    };
    return labels[category] || category;
  };

  const getFilteredHistory = () => {
    if (activeTab === 'consultation') {
      return history.filter(item =>
        !item.category ||
        !['contract_analysis', 'research', 'jurisprudence'].includes(item.category)
      );
    } else if (activeTab === 'contract_analysis') {
      return history.filter(item => item.category === 'contract_analysis');
    } else if (activeTab === 'research') {
      return history.filter(item =>
        item.category === 'research' || item.category === 'jurisprudence'
      );
    }
    return history;
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
    if (newValue === 'documents' && docHistory.length === 0 && !docLoading) {
      loadDocHistory();
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('cs-CZ', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const truncateQuestion = (question: string, maxLength = 60) => {
    if (question.length <= maxLength) return question;
    return question.substring(0, maxLength) + '...';
  };

  const handleItemClick = async (item: ConsultationHistoryItem) => {
    // All items (including jurisprudence) should use the parent's handler
    // The parent page will display results appropriately
    onSelectConversation(item.id);
  };

  if (activeTab !== 'documents' && loading && history.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress size={32} sx={{ color: '#1F1F1F' }} />
        <Typography variant="body2" sx={{ mt: 2, color: '#666666', fontWeight: 500 }}>
          Načítání historie...
        </Typography>
      </Box>
    );
  }

  if (activeTab !== 'documents' && error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="info"
          sx={{
            borderRadius: '8px',
            '& .MuiAlert-message': {
              color: '#1F1F1F'
            }
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (activeTab !== 'documents' && history.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <HistoryIcon sx={{ fontSize: 56, color: '#CCCCCC', mb: 2 }} />
        <Typography variant="body1" sx={{ color: '#1F1F1F', fontWeight: 600, mb: 1 }}>
          Zatím nemáte žádné konzultace
        </Typography>
        <Typography variant="body2" sx={{ color: '#666666' }}>
          Vaše budoucí konzultace se zde uloží
        </Typography>
      </Box>
    );
  }

  const filteredHistory = getFilteredHistory();

  return (
    <Paper elevation={0} sx={{
      height: '100%',
      overflow: 'hidden',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: '12px'
    }}>
      <Box
        sx={{
          p: 2.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#FAFAFA'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <HistoryIcon sx={{ color: '#1F1F1F' }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1F1F1F' }}>
            Historie
          </Typography>
          <Chip
            label={filteredHistory.length}
            size="small"
            sx={{
              bgcolor: '#1F1F1F',
              color: '#FFFFFF',
              fontWeight: 600,
              minWidth: '32px'
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => {
              if (activeTab === 'documents') {
                loadDocHistory();
              } else {
                loadHistory();
              }
            }}
            title="Obnovit"
            sx={{ '&:hover': { bgcolor: 'rgba(31,31,31,0.08)' } }}
          >
            <HistoryIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          sx={{
            '&:hover': {
              bgcolor: 'rgba(31, 31, 31, 0.08)'
            }
          }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        {/* Tabs for filtering */}
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            minHeight: '52px',
            bgcolor: '#FFFFFF',
            '& .MuiTab-root': {
              minHeight: '52px',
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: '#666666',
              transition: 'all 0.2s ease',
              '&:hover': {
                color: '#1F1F1F',
                bgcolor: 'rgba(31, 31, 31, 0.04)'
              },
              '&.Mui-selected': {
                color: '#1F1F1F',
                fontWeight: 600
              }
            },
            '& .MuiTabs-indicator': {
              backgroundColor: '#1F1F1F',
              height: '3px',
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label="Právní konzultace" value="consultation" />
          <Tab label="Analýza smluv" value="contract_analysis" />
          <Tab label="Právní výzkum" value="research" />
          <Tab label="Dokumenty" value="documents" />
        </Tabs>

        {activeTab === 'documents' ? (
          <List sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto', p: 0 }}>
            {docLoading && docHistory.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : docError ? (
              <Box sx={{ p: 2 }}>
                <Alert severity="error">{docError}</Alert>
              </Box>
            ) : docHistory.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">Žádné vygenerované dokumenty</Typography>
              </Box>
            ) : (
              docHistory.map((doc, index) => (
                <React.Fragment key={doc.filename}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => window.open(doc.download_url, '_blank')}
                      sx={{ py: 1.5, px: 2.5 }}
                    >
                      <Description sx={{ mr: 1, color: '#1F1F1F' }} />
                      <ListItemText
                        primary={
                          <Typography variant="body2" fontWeight={600} sx={{ color: '#1F1F1F' }}>
                            {doc.contract_type.toUpperCase()} — {doc.filename}
                          </Typography>
                        }
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {new Date(doc.generated_at).toLocaleString('cs-CZ')}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  {index < docHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </List>
        ) : (
          <List sx={{ maxHeight: 'calc(100vh - 400px)', overflow: 'auto', p: 0 }}>
          {filteredHistory.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={selectedConversationId === item.id}
                  onClick={() => handleItemClick(item)}
                  sx={{
                    py: 2.5,
                    px: 2.5,
                    transition: 'all 0.2s ease',
                    borderLeft: '3px solid transparent',
                    '&:hover': {
                      bgcolor: 'rgba(31, 31, 31, 0.04)',
                      borderLeftColor: '#1F1F1F',
                      transform: 'translateX(2px)'
                    },
                    '&.Mui-selected': {
                      bgcolor: 'rgba(31, 31, 31, 0.08)',
                      borderLeft: '4px solid',
                      borderColor: '#1F1F1F',
                      '&:hover': {
                        bgcolor: 'rgba(31, 31, 31, 0.12)'
                      }
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          mb: 1,
                          color: '#1F1F1F',
                          lineHeight: 1.4
                        }}
                      >
                        {truncateQuestion(item.original_question)}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Box sx={{ display: 'flex', gap: 0.75, mb: 1, flexWrap: 'wrap' }}>
                          {item.category && !['contract_analysis', 'research', 'jurisprudence'].includes(item.category) && (
                            <Chip
                              label={getCategoryLabel(item.category)}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                borderColor: '#1F1F1F',
                                color: '#1F1F1F',
                                fontWeight: 500
                              }}
                            />
                          )}
                          <Chip
                            label={getStatusLabel(item.status)}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              bgcolor: item.status === 'completed' ? '#4caf50' : '#e0e0e0',
                              color: item.status === 'completed' ? '#FFFFFF' : '#666666'
                            }}
                          />
                          {item.confidence_level && item.has_final_response && (
                            <Chip
                              label={`Spolehlivost: ${item.confidence_level}`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: 'rgba(31, 31, 31, 0.08)',
                                color: '#1F1F1F',
                                fontWeight: 500
                              }}
                            />
                          )}
                          {/* Show result count for jurisprudence searches */}
                          {(item.category === 'jurisprudence' || item.category === 'research') && item.metadata?.total_results !== undefined && (
                            <Chip
                              label={`${item.metadata.total_results} výsledků`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.7rem',
                                bgcolor: '#2196f3',
                                color: '#FFFFFF',
                                fontWeight: 500
                              }}
                            />
                          )}
                        </Box>
                        <Typography variant="caption" sx={{ color: '#666666', fontSize: '0.75rem' }}>
                          {formatDate(item.created_at)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
              {index < history.length - 1 && <Divider />}
            </React.Fragment>
          ))}
          </List>
        )}
      </Collapse>
    </Paper>
  );
};

export default ConsultationHistory;
